import type { AnnouncementFollowupRecipient, AnnouncementManagementRow } from './announcements'

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}

function formatDate(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR')
}

function statusLabel(status: AnnouncementFollowupRecipient['status']) {
  if (status === 'ciente') return 'Ciente'
  if (status === 'visualizado') return 'Visualizou, sem ciência'
  return 'Não visualizou'
}

export function printAnnouncementReport(
  announcement: AnnouncementManagementRow,
  recipients: AnnouncementFollowupRecipient[],
  audienceLabel: string,
) {
  const popup = window.open('', '_blank', 'width=1100,height=800')
  if (!popup) throw new Error('O navegador bloqueou a janela do relatório. Permita pop-ups para o Suite e tente novamente.')

  popup.opener = null

  const generatedAt = new Date().toLocaleString('pt-BR')
  const priority = announcement.priority === 'critico' ? 'Crítico' : announcement.priority === 'alerta' ? 'Alerta' : 'Informação'
  const notSeen = recipients.filter((item) => item.status === 'nao_visualizado').length
  const seenNoAck = recipients.filter((item) => item.status === 'visualizado').length
  const acknowledged = recipients.filter((item) => item.status === 'ciente').length

  const rows = recipients.map((item) => `
    <tr>
      <td><strong>${escapeHtml(item.nome)}</strong><br><small>${escapeHtml(item.email)}</small></td>
      <td>${escapeHtml(item.unit_label)}</td>
      <td>${escapeHtml(statusLabel(item.status))}</td>
      <td>${escapeHtml(formatDate(item.first_seen_at))}</td>
      <td>${escapeHtml(formatDate(item.acknowledged_at))}</td>
    </tr>
  `).join('')

  popup.document.write(`<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>Relatório de Comunicado - ${escapeHtml(announcement.reference_code || announcement.title)}</title>
<style>
  @page { size: A4; margin: 16mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #102b3d; background: #fff; font-size: 11px; }
  .toolbar { display: flex; justify-content: flex-end; margin-bottom: 16px; }
  .toolbar button { padding: 9px 14px; border: 0; border-radius: 8px; background: #009fbd; color: #fff; font-weight: 700; cursor: pointer; }
  .brand { color: #0088a8; font-size: 10px; font-weight: 800; letter-spacing: 1.6px; }
  h1 { margin: 5px 0 2px; font-size: 23px; color: #071f33; }
  .subtitle { color: #627584; margin-bottom: 18px; }
  .meta { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px 18px; padding: 13px; background: #f3f7f9; border-radius: 10px; }
  .meta div span { display: block; color: #657b89; font-size: 9px; text-transform: uppercase; letter-spacing: .6px; }
  .meta div strong { display: block; margin-top: 3px; font-size: 11px; }
  .message { margin: 16px 0; padding: 14px; border: 1px solid #dce6eb; border-left: 4px solid #00a9c6; border-radius: 10px; white-space: pre-wrap; line-height: 1.5; }
  .metrics { display: grid; grid-template-columns: repeat(6, 1fr); gap: 7px; margin: 16px 0; }
  .metric { padding: 10px 8px; border: 1px solid #dce6eb; border-radius: 9px; text-align: center; }
  .metric b { display: block; font-size: 17px; color: #071f33; }
  .metric span { display: block; margin-top: 3px; color: #627584; font-size: 8px; }
  h2 { margin: 20px 0 9px; font-size: 14px; color: #071f33; }
  table { width: 100%; border-collapse: collapse; font-size: 9px; }
  th { text-align: left; padding: 7px; background: #eaf4f7; border-bottom: 1px solid #cadbe2; }
  td { padding: 7px; border-bottom: 1px solid #e5ecef; vertical-align: top; }
  td small { color: #657b89; }
  .footer { margin-top: 16px; padding-top: 10px; border-top: 1px solid #dce6eb; color: #71828d; font-size: 8px; line-height: 1.4; }
  @media print { .toolbar { display: none; } body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
</style>
</head>
<body>
  <div class="toolbar"><button onclick="window.print()">Imprimir / Salvar como PDF</button></div>
  <div class="brand">SUITE DE PROCEDIMENTOS · COMUNICAÇÃO OPERACIONAL</div>
  <h1>Relatório de acompanhamento de comunicado</h1>
  <div class="subtitle">Registro de alcance, leitura e ciência da orientação operacional.</div>

  <section class="meta">
    <div><span>Título</span><strong>${escapeHtml(announcement.title)}</strong></div>
    <div><span>Referência</span><strong>${escapeHtml(announcement.reference_code || 'Sem referência')}</strong></div>
    <div><span>Prioridade</span><strong>${escapeHtml(priority)}</strong></div>
    <div><span>Público</span><strong>${escapeHtml(audienceLabel)}</strong></div>
    <div><span>Publicado em</span><strong>${escapeHtml(formatDate(announcement.created_at))}</strong></div>
    <div><span>Relatório gerado em</span><strong>${escapeHtml(generatedAt)}</strong></div>
  </section>

  <div class="message">${escapeHtml(announcement.body || '')}</div>

  <section class="metrics">
    <div class="metric"><b>${announcement.recipients}</b><span>Destinatários</span></div>
    <div class="metric"><b>${announcement.viewed}</b><span>Visualizações</span></div>
    <div class="metric"><b>${Number(announcement.viewed_pct).toLocaleString('pt-BR')}%</b><span>Leitura</span></div>
    <div class="metric"><b>${announcement.acknowledged}</b><span>Ciências</span></div>
    <div class="metric"><b>${Number(announcement.acknowledged_pct).toLocaleString('pt-BR')}%</b><span>Ciência</span></div>
    <div class="metric"><b>${announcement.avg_ack_minutes == null ? '—' : `${Number(announcement.avg_ack_minutes).toLocaleString('pt-BR')} min`}</b><span>Tempo médio</span></div>
  </section>

  <p><strong>Situação atual:</strong> ${notSeen} não visualizaram · ${seenNoAck} visualizaram sem ciência · ${acknowledged} cientes.</p>

  <h2>Acompanhamento nominal</h2>
  <table>
    <thead><tr><th>Colaborador</th><th>Unidade</th><th>Situação</th><th>Visualizou</th><th>Ciência</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="footer">
    Documento gerado pelo Suite de Procedimentos. A ciência é registrada pelo próprio colaborador no Centro de Comunicados. Este relatório representa o estado do acompanhamento no momento de sua geração.
  </div>

  <script>window.addEventListener('load', () => setTimeout(() => window.print(), 250))</script>
</body>
</html>`)
  popup.document.close()
}
